import React from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import AnimatedNumber from '../../../Components/AnimatedNumber';

function MiniChart({ data, color, height = 40 }) {
    const values = (data || '').split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
    if (values.length === 0) return null;
    const max = Math.max(...values);
    const barWidth = 100 / values.length;

    return (
        <svg width="100%" height={height} className="mt-2">
            {values.map((v, i) => {
                const barH = max > 0 ? (v / max) * height : 0;
                return (
                    <rect
                        key={i}
                        x={`${i * barWidth + barWidth * 0.15}%`}
                        y={height - barH}
                        width={`${barWidth * 0.7}%`}
                        height={barH}
                        rx={2}
                        fill={hexToRgba(color, 0.6)}
                    />
                );
            })}
        </svg>
    );
}

function ChangeBadge({ change, change_type }) {
    if (!change) return null;
    const colors = {
        positive: 'bg-green-100 text-green-700',
        negative: 'bg-red-100 text-red-700',
        neutral: 'bg-gray-100 text-gray-600',
    };
    const cls = colors[change_type] || colors.neutral;
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
            {change}
        </span>
    );
}

export default function MetricDashboardSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'cards';

    if (items.length === 0) return null;

    const renderPanel = (item, index) => {
        const isGlass = variant === 'glass';
        const cardStyle = isGlass
            ? { background: hexToRgba('#ffffff', 0.05), backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.1)' }
            : { background: hexToRgba(s.accent, 0.05), border: `1px solid ${hexToRgba(s.accent, 0.1)}` };

        return (
            <div
                key={index}
                className="rounded-xl p-5 flex flex-col justify-between"
                style={cardStyle}
            >
                <div>
                    <p className="text-sm font-medium mb-1" style={{ color: isGlass ? 'rgba(255,255,255,0.7)' : s.textSub }}>
                        {item.title}
                    </p>
                    <div className="flex items-end gap-2 mb-1">
                        <span className="text-3xl font-bold" style={{ color: isGlass ? '#fff' : s.text }}>
                            {item.prefix}
                            <AnimatedNumber value={item.value} enableAnimations={enableAnimations} visible={visible} />
                            {item.suffix}
                        </span>
                        <ChangeBadge change={item.change} change_type={item.change_type} />
                    </div>
                </div>
                <MiniChart data={item.chart_data} color={s.accent} />
            </div>
        );
    };

    const sectionBg = variant === 'glass' ? { background: '#0f172a' } : {};

    return (
        <section ref={ref} style={sectionBg}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div className={`max-w-6xl mx-auto px-4 py-16 ${anim}`}>
                <SectionHeader
                    title={content.title}
                    subtitle={content.subtitle}
                    tag={content.tag}
                    s={s}
                    primaryColor={primaryColor}
                />

                {variant === 'unified' ? (
                    <div
                        className="rounded-xl overflow-hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-x divide-y md:divide-y-0"
                        style={{
                            background: hexToRgba(s.accent, 0.03),
                            border: `1px solid ${hexToRgba(s.accent, 0.1)}`,
                            '--tw-divide-opacity': 0.1,
                        }}
                    >
                        {items.slice(0, 4).map((item, i) => (
                            <div key={i} className="p-5 flex flex-col justify-between">
                                <div>
                                    <p className="text-sm font-medium mb-1" style={{ color: s.textSub }}>
                                        {item.title}
                                    </p>
                                    <div className="flex items-end gap-2 mb-1">
                                        <span className="text-3xl font-bold" style={{ color: s.text }}>
                                            {item.prefix}
                                            <AnimatedNumber value={item.value} enableAnimations={enableAnimations} visible={visible} />
                                            {item.suffix}
                                        </span>
                                        <ChangeBadge change={item.change} change_type={item.change_type} />
                                    </div>
                                </div>
                                <MiniChart data={item.chart_data} color={s.accent} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {items.slice(0, 4).map((item, i) => renderPanel(item, i))}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
